/**
 * Landing page section registry.
 *
 * Defines the ordered list of sections rendered on the landing page.
 * Each entry maps a section id to its React component and background
 * variant. The {@link LandingPage} component iterates this registry
 * to assemble the full page.
 *
 * @module landing/section-registry
 */

import React from 'react';
import { HeroSection } from './sections/HeroSection';
import { PlanTypesSection } from './sections/PlanTypesSection';
import { BenefitsSection } from './sections/BenefitsSection';
import { HowItWorksSection } from './sections/HowItWorksSection';
import { CtaSection } from './sections/CtaSection';
import { FooterSection } from './sections/FooterSection';

/** Describes a single landing page section. */
export interface SectionDescriptor {
  /** HTML id attribute for the section wrapper. */
  id: string;
  /** React component to render inside the section. */
  component: React.ComponentType;
  /** Background variant applied via CSS modifier class. */
  background: 'default' | 'alt' | 'primary' | 'accent';
}

/** Ordered list of landing page sections. */
export const LANDING_SECTIONS: SectionDescriptor[] = [
  { id: 'hero', component: HeroSection, background: 'default' },
  { id: 'plan-types', component: PlanTypesSection, background: 'default' },
  { id: 'benefits', component: BenefitsSection, background: 'alt' },
  { id: 'how-it-works', component: HowItWorksSection, background: 'default' },
  { id: 'cta', component: CtaSection, background: 'accent' },
  { id: 'footer', component: FooterSection, background: 'primary' },
];
